import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { setNavigateFunction } from '@/utils/navigation';
import Layout from '@/components/layout/Layout';
import ClientLayout from '@/components/layout/ClientLayout';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Settings from '@/pages/Settings';
import Chat from '@/pages/Chat/Chat';
import { CommandPalette } from '@/components/ui/CommandPalette';
import { useCommandPalette } from '@/hooks/useCommandPalette';
import SuperAdminAdmins from '@/pages/SuperAdmin/Admins';
import SuperAdminPurge from '@/pages/SuperAdmin/Purge';
import SuperAdminPipelines from '@/pages/SuperAdmin/Pipelines';
import { apiService } from '@/services/api';
import InvoiceList from '@/pages/InvoiceList';
import InvoiceViewer from '@/pages/InvoiceViewer';

// Module Achat
import ClientsListTailwind from '@/pages/Achat/ClientsListTailwind';
import ConsumablesTailwind from '@/pages/Achat/ConsumablesTailwind';
import AchatClientPricingTailwind from '@/pages/Achat/ClientPricingTailwind';
import PurchaseOrdersTailwind from '@/pages/Achat/PurchaseOrdersTailwind';
import PurchaseOrderDetail from '@/pages/Achat/PurchaseOrderDetail';
import AdminNewOrder from '@/pages/Achat/AdminNewOrder';
import AchatDashboardTailwind from '@/pages/Achat/AchatDashboardTailwind';
import UploadPurchaseOrderPDF from '@/pages/Achat/UploadPurchaseOrderPDF';
import DuplicateOrderTailwind from '@/pages/Achat/DuplicateOrderTailwind';
import SalesPointsTailwind from '@/pages/Achat/SalesPointsTailwind';
import ProspectsTailwind from '@/pages/Achat/ProspectsTailwind';
import CentralManagementTailwind from '@/pages/Achat/CentralManagementTailwind';
import ClientPortalTailwind from '@/pages/Achat/ClientPortalTailwind';
import DeliveryFeeConfigs from '@/pages/Admin/DeliveryFeeConfigs';
import ClientDetail from './pages/Achat/ClientDetail';
import MyReminders from '@/pages/Achat/MyReminders';
import ProductConfig from '@/pages/Achat/ProductConfig';
// Module Client (Portail Client)
import ClientOrdersTailwind from '@/pages/Client/ClientOrdersTailwind';
import ClientOrderDetail from '@/pages/Client/ClientOrderDetailNew';
import ClientCatalogTailwind from '@/pages/Client/ClientCatalogTailwind';
import ClientInvoicesTailwind from '@/pages/Client/ClientInvoicesTailwind';
import ClientSalesPointsTailwind from '@/pages/Client/ClientSalesPointsTailwind';
import ClientAddressesTailwind from '@/pages/Client/ClientAddressesTailwind';
import ClientDashboardTailwind from '@/pages/Client/ClientDashboardTailwind';
import ClientPricingTailwind from '@/pages/Client/ClientPricingTailwind';
import ClientNewOrderTailwind from '@/pages/Client/ClientNewOrderTailwind';
import ClientSettings from '@/pages/Client/ClientSettings';
import ClientMembers from '@/pages/Client/ClientMembers';
import MyMemberBuyers from '@/pages/Client/MyMemberBuyers';
import MyAdherents from '@/pages/Client/MyAdherents';
import ClientDocumentation from '@/pages/Client/ClientDocumentation';
import ClientRegister from '@/pages/Client/ClientRegister';
import Administration from '@/pages/Client/Administration';
import UserProfile from '@/pages/Administration/UserProfile';
import Documentation from '@/pages/Documentation';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import ClientForgotPassword from '@/pages/Client/ClientForgotPassword';
import ClientResetPassword from '@/pages/Client/ClientResetPassword';
import VerifyEmail from '@/pages/Auth/VerifyEmail';
import SetPassword from '@/pages/Auth/SetPassword';
import ResendVerification from '@/pages/Auth/ResendVerification';

// Module Commissions
import CommissionDashboard from '@/pages/Commissions/CommissionDashboard';
import CommissionSuperDashboard from '@/pages/Commissions/CommissionSuperDashboard';
import CommissionConfig from '@/pages/Commissions/CommissionConfig';
import CommissionValidation from '@/pages/Commissions/CommissionValidation';

import '@/components/ui/PhoneInput.css';

function App() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const currentUser = useAuthStore((state) => state.user);
  const isLoading = useAuthStore((state) => state.isLoading);
  const setUser = useAuthStore((state) => state.setUser);
  const location = useLocation();
  const navigate = useNavigate();
  const { open, setOpen } = useCommandPalette();

  // Set up navigation function for API service
  useEffect(() => {
    setNavigateFunction(navigate);
  }, [navigate]);

  // Clear stale user data on app initialization if token doesn't match
  useEffect(() => {
    const checkAuthState = async () => {
      if (isAuthenticated && currentUser && !isLoading) {
        try {
          // Verify the current user data is still valid
          const freshUser = await apiService.getCurrentUser();
          if (freshUser.role !== currentUser.role) {
            console.log('[APP] User role mismatch, updating user data');
            setUser(freshUser);
          }
        } catch (error) {
          console.log('[APP] Auth verification failed, clearing auth state');
          // Token is invalid, clear auth state
          useAuthStore.getState().logout();
        }
      }
    };

    // Only run this check once on app load
    checkAuthState();
  }, []); // Empty dependency array - only run once

  // Ensure we have the current user (with role) after page refresh or initial load
  useEffect(() => {
    const loadMe = async () => {
      try {
        // Don't interfere if we're currently in a login process
        if (isLoading) return;

        if (isAuthenticated && (!currentUser || !currentUser.role)) {
          const me = await apiService.getCurrentUser();
          setUser(me);
        }
      } catch (e) {
        console.error('Failed to load current user:', e);
      }
    };
    loadMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);

  // Routes publiques (accessibles sans authentification)
  const publicRoutes = ['/login', '/register', '/client/register', '/forgot-password', '/reset-password', '/client/forgot-password', '/buyer/reset-password', '/verify-email', '/set-password', '/resend-verification'];
  const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));

  // Si non authentifié et pas sur une route publique, rediriger vers login
  if (!isAuthenticated && !isPublicRoute) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/client/register" element={<ClientRegister />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Si authentifié, on continue le rendu normal (les routes publiques comme /lp/:slug restent accessibles plus bas)

  // Si non authentifié et sur route publique, afficher la route publique
  if (!isAuthenticated && isPublicRoute) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/client/register" element={<ClientRegister />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/client/forgot-password" element={<ClientForgotPassword />} />
        <Route path="/buyer/reset-password" element={<ClientResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/resend-verification" element={<ResendVerification />} />
      </Routes>
    );
  }

  // Redirection si l'email n'est pas vérifié (pour les clients et membres)
  const isVerificationRoute = location.pathname === '/resend-verification' || location.pathname === '/verify-email';
  if (isAuthenticated && currentUser && !currentUser.is_email_verified &&
    (currentUser.role === 'client' || currentUser.role === 'client_member') &&
    !isVerificationRoute) {
    return <Navigate to="/resend-verification" replace state={{ unverified: true }} />;
  }

  // Redirection basée sur le rôle
  if (isAuthenticated && currentUser?.role && location.pathname === '/') {
    switch (currentUser.role) {
      case 'client':
      case 'client_member':
        return <Navigate to="/client/dashboard" replace />;
      case 'admin':
      case 'super_admin':
      default:
        return <Navigate to="/achat/dashboard" replace />;
    }
  }

  // Si en cours de chargement ou authentifié mais currentUser pas encore chargé, afficher un loader
  if (isLoading || (isAuthenticated && !currentUser)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 transition-colors">
      <Routes>
        {/* Routes Client avec ClientLayout */}
        <Route path="/client" element={<ClientLayout />}>
          <Route index element={<Navigate to="/client/dashboard" replace />} />
          <Route path="dashboard" element={<ClientDashboardTailwind />} />
          <Route path="catalog" element={<ClientCatalogTailwind />} />
          <Route path="pricing" element={<ClientPricingTailwind />} />
          <Route path="orders" element={<ClientOrdersTailwind />} />
          <Route path="orders/new" element={<ClientNewOrderTailwind />} />
          <Route path="orders/:id" element={<ClientOrderDetail />} />
          <Route path="sales-points" element={<ClientSalesPointsTailwind />} />
          <Route path="members" element={<ClientMembers />} />
          <Route path="my-members" element={<MyMemberBuyers />} />
          <Route path="my-adherents" element={<MyAdherents />} />
          <Route path="addresses" element={<ClientAddressesTailwind />} />
          <Route path="invoices" element={<ClientInvoicesTailwind />} />
          <Route path="settings" element={<ClientSettings />} />
          <Route path="documentation" element={<ClientDocumentation />} />
          <Route path="administration" element={<Administration />} />
          <Route path="portal" element={<ClientPortalTailwind />} />
          <Route path="chat" element={<Chat />} />
        </Route>

        {/* Routes principales avec Layout normal - Admin, Super Admin et Apporteur */}
        {(currentUser?.role === 'admin' || currentUser?.role === 'super_admin' || currentUser?.role === 'apporteur') && (
          <Route path="/" element={<Layout />}>
            <Route index element={<Navigate to="/achat/dashboard" replace />} />
            <Route path="chat" element={<Chat />} />
            <Route path="invoices" element={<InvoiceList />} />
            <Route path="invoices/:invoiceId" element={<InvoiceViewer />} />
            <Route path="settings" element={<Settings />} />
            <Route path="documentation" element={<Documentation />} />
            <Route path="administration/users/:userType/:userId" element={<UserProfile />} />

            {/* Module Administration - Admin et Super Admin uniquement */}
            {(currentUser?.role === 'admin' || currentUser?.role === 'super_admin') && (
              <Route path="administration" element={<Administration />} />
            )}

            {/* Module Achat - Admin et Super Admin */}
            <Route path="achat/dashboard" element={<AchatDashboardTailwind />} />
            <Route path="achat/prospects" element={<ProspectsTailwind />} />
            <Route path="achat/clients" element={<ClientsListTailwind />} />
            <Route path="achat/clients/:id" element={<ClientDetail />} />
            <Route path="achat/sales-points" element={<SalesPointsTailwind />} />
            <Route path="achat/consumables" element={<ConsumablesTailwind />} />
            <Route path="achat/pricing" element={<AchatClientPricingTailwind />} />
            <Route path="achat/orders" element={<PurchaseOrdersTailwind />} />
            <Route path="achat/orders/new" element={<AdminNewOrder />} />
            <Route path="achat/orders/upload-pdf" element={<UploadPurchaseOrderPDF />} />
            <Route path="achat/orders/:id" element={<PurchaseOrderDetail />} />
            <Route path="achat/orders/:orderId/duplicate" element={<DuplicateOrderTailwind />} />
            <Route path="achat/centrals" element={<CentralManagementTailwind />} />
            <Route path="achat/delivery-fees" element={<DeliveryFeeConfigs />} />
            <Route path="achat/product-config" element={<ProductConfig />} />
            <Route path="achat/invoices" element={<InvoiceList />} />
            <Route path="achat/reminders" element={<MyReminders />} />

            {/* Module Commissions - Admin et Super Admin */}
            <Route path="commissions" element={<Navigate to="/commissions/dashboard" replace />} />
            <Route path="commissions/dashboard" element={<CommissionDashboard />} />
            <Route path="commissions/super-dashboard" element={<CommissionSuperDashboard />} />
            <Route path="commissions/config" element={<CommissionConfig />} />
            <Route path="commissions/validation" element={<CommissionValidation />} />

            {/* Module Prospects - Admin et Super Admin */}
            <Route path="prospects" element={<ProspectsTailwind />} />


            {currentUser?.role === 'super_admin' && (
              <>
                <Route path="super-admin/admins" element={<SuperAdminAdmins />} />
                <Route path="super-admin/pipelines" element={<SuperAdminPipelines />} />
                <Route path="super-admin/purge" element={<SuperAdminPurge />} />
              </>
            )}
          </Route>
        )}


        <Route path="*" element={
          <Navigate to={
            currentUser?.role === 'client' || currentUser?.role === 'client_member'
              ? '/client/dashboard'
              : '/achat/dashboard'
          } replace />
        } />
      </Routes>
      {/* Command Palette global - accessible via ⌘K */}
      {isAuthenticated && (
        <CommandPalette open={open} onOpenChange={setOpen} />
      )}
    </div>
  );
}

export default App;
